import { SeeqNames } from 'generated-seeqnames/app.constants.seeqnames';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { PersistenceLevel } from '@/core/flux.service';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { BaseToolPersistedState } from '@/toolSelection/baseTool.store';
import type { DerivedFeatureWithColumnDefinition, NewlyAddedInputFeature } from '@/tools/models/template.types';
import type { NewlyAddedDerivedFeature } from '@/tools/models/template.types';
import type { TemplateDerivedFeature } from '@/tools/models/template.types';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { InputFeatureV1, OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import type { ReferencedOutputItemV1 } from '@seeqdev/api/model/ReferencedOutputItemV1';
import type { TemplateOutputV1 } from '@seeqdev/api/model/TemplateOutputV1';

export interface TemplateInput {
  name: string;
  description?: string;
  referenceId?: string;
  id?: string;
  canonicalId?: string;
  isCollapsed?: boolean;
  originalName?: string;
  columnDefinition?: ColumnDefinition;
  outputType?: OutputTypeEnum;
  inputType?: string;
  parent?: string;
  inputItem?: string;
  inputNumeric?: number;
  inputNumericUom?: string;
  inputText?: string;
  inputBoolean?: boolean;
  isMapped?: boolean;
  mappedFeatureId?: string;
  selectedItem?: { name: string; id: string };
}

export interface TemplateOutput extends ReferencedOutputItemV1 {
  id?: string;
  displayName?: string;
}

export type TemplateItem = {
  name: string;
  description: string;
  derivedFeatures: ColumnDefinition[] | TemplateOutput[] | DerivedFeatureV1[] | DerivedFeatureWithColumnDefinition[];
  inputFeatures: TemplateInput[];
  existingDerivedFeatures: TemplateDerivedFeature[];
  existingInputFeatures: TemplateInput[];
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  newInputFeatures: NewlyAddedInputFeature[];
};

// TODO: CRAB-56692: once the edit flow in Scaling Tables has updated types, remove derivedFeatures and
//  inputFeatures and just use existingDerivedFeatures and existingInputFeatures.
// TODO: CRAB-56765: also update types in create and apply files to use existing/new feature fields instead of derived/input features.
export type TemplatePayload = Partial<
  Pick<
    TemplateItem,
    | 'name'
    | 'description'
    | 'inputFeatures'
    | 'derivedFeatures'
    | 'existingDerivedFeatures'
    | 'existingInputFeatures'
    | 'newDerivedFeatures'
    | 'newInputFeatures'
  >
>;

interface TemplateState extends BaseToolPersistedState {
  /** The template container GUID — the stable, canonical identity of the template across versions. */
  id: string;
  /** The GUID of the specific template version loaded in the store (e.g. a draft or the initial unlocked template). Used by the save flow to target editModelVersion and publishModelVersion. */
  versionId: string;
  description: string;
  inputFeatures: TemplateInput[];
  derivedFeatures: TemplateOutput[] | ColumnDefinition[] | DerivedFeatureV1[] | DerivedFeatureWithColumnDefinition[];
  name: string;
  scopedTo: string | undefined;
  datasourceId: string | undefined;
  featureToEditId: string | undefined;
  instanceToEditId: string | undefined;
  existingDerivedFeatures: TemplateDerivedFeature[];
  existingInputFeatures: TemplateInput[];
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  newInputFeatures: NewlyAddedInputFeature[];
  instanceNameFeature: TemplateInput | undefined;
  /** Whether the loaded template version is locked (immutable). */
  isLocked: boolean;
  /** Whether the loaded template is a draft (as opposed to the initial unlocked template). */
  isDraft: boolean;
  /** The production version number this draft was created from (e.g. 1 for "draft from v1"). */
  sourceVersionNumber: number | undefined;
  /** Canonical feature IDs in the template version this draft was created from. Undefined when not loaded/unknown. */
  editBaseFeatureCanonicalIds: string[] | undefined;
  /** The total number of instances for this template (across all versions). */
  instanceCount: number | undefined;
}

export type TemplateDehydratedState = TemplateState;

export class TemplateStore extends BaseToolStore {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';

  type = TREND_TOOLS.MODEL_CREATE;

  initialize(): void {
    const initialState: TemplateState = {
      ...BASE_TOOL_COMMON_PROPS,
      id: '',
      versionId: '',
      description: '',
      inputFeatures: [],
      derivedFeatures: [],
      name: '',
      scopedTo: undefined,
      datasourceId: undefined,
      featureToEditId: undefined,
      instanceToEditId: undefined,
      existingDerivedFeatures: [],
      existingInputFeatures: [],
      newDerivedFeatures: [],
      newInputFeatures: [],
      instanceNameFeature: undefined,
      isLocked: false,
      isDraft: false,
      sourceVersionNumber: undefined,
      editBaseFeatureCanonicalIds: undefined,
      instanceCount: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get id(): string {
    return this.state.get('id');
  }

  get versionId(): string {
    return this.state.get('versionId');
  }

  get name(): string {
    return this.state.get('name');
  }

  get scopedTo(): string | undefined {
    return this.state.get('scopedTo');
  }

  get datasourceId(): string | undefined {
    return this.state.get('datasourceId');
  }

  get description(): string {
    return this.state.get('description');
  }

  get derivedFeatures():
    | TemplateOutput[]
    | ColumnDefinition[]
    | DerivedFeatureV1[]
    | DerivedFeatureWithColumnDefinition[] {
    return this.state.get('derivedFeatures');
  }

  get inputFeatures(): TemplateInput[] {
    return this.state.get('inputFeatures');
  }

  get featureToEditId(): string | undefined {
    return this.state.get('featureToEditId');
  }

  get instanceToEditId(): string | undefined {
    return this.state.get('instanceToEditId');
  }

  get existingDerivedFeatures(): TemplateDerivedFeature[] {
    return this.state.get('existingDerivedFeatures');
  }

  get existingInputFeatures(): TemplateInput[] {
    return this.state.get('existingInputFeatures');
  }

  get newDerivedFeatures(): NewlyAddedDerivedFeature[] {
    return this.state.get('newDerivedFeatures');
  }

  get newInputFeatures(): NewlyAddedInputFeature[] {
    return this.state.get('newInputFeatures');
  }

  get instanceNameFeature(): TemplateInput | undefined {
    return this.state.get('instanceNameFeature');
  }

  get isLocked(): boolean {
    return this.state.get('isLocked');
  }

  get isDraft(): boolean {
    return this.state.get('isDraft');
  }

  get sourceVersionNumber(): number | undefined {
    return this.state.get('sourceVersionNumber');
  }

  get editBaseFeatureCanonicalIds(): string[] | undefined {
    return this.state.get('editBaseFeatureCanonicalIds');
  }

  get instanceCount(): number | undefined {
    return this.state.get('instanceCount');
  }

  dehydrate(): TemplateDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<TemplateDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
  };

  resetTemplate = (): void => {
    this.initialize();
  };

  setFeatureToEditId = ({ featureToEditId }: { featureToEditId: string | undefined }): void => {
    this.state.set('featureToEditId', featureToEditId);
  };

  setInstanceToEditId = ({ instanceToEditId }: { instanceToEditId: string | undefined }): void => {
    this.state.set('instanceToEditId', instanceToEditId);
  };

  setVersionState = ({
    isLocked,
    isDraft,
    sourceVersionNumber,
    editBaseFeatureCanonicalIds,
  }: {
    isLocked: boolean;
    isDraft: boolean;
    sourceVersionNumber?: number;
    editBaseFeatureCanonicalIds: string[] | undefined;
  }): void => {
    this.state.set('isLocked', isLocked);
    this.state.set('isDraft', isDraft);
    this.state.set('sourceVersionNumber', sourceVersionNumber);
    this.state.set('editBaseFeatureCanonicalIds', editBaseFeatureCanonicalIds);
  };

  setInstanceCount = ({ instanceCount }: { instanceCount: number | undefined }): void => {
    this.state.set('instanceCount', instanceCount);
  };

  /** Sets the template output from the API response. */
  setTemplateOutput = ({ templateOutput }: { templateOutput: TemplateOutputV1 }): void => {
    const { description, name, id, inputFeatures, derivedFeatures, scopedTo, versionId, datasourceId } = templateOutput;
    this.state.set('name', name);
    this.state.set('id', id);
    this.state.set('versionId', versionId ?? '');
    this.state.set('description', description);
    this.state.set('inputFeatures', inputFeatures);
    this.state.set('derivedFeatures', derivedFeatures);
    this.state.set('scopedTo', scopedTo);
    this.state.set('datasourceId', datasourceId);
    this.state.set('existingDerivedFeatures', derivedFeatures);
    this.state.set(
      'existingInputFeatures',
      inputFeatures?.filter((feature) => feature.name !== SeeqNames.Models.InstanceNameInputFeatureName),
    );
    this.state.set(
      'instanceNameFeature',
      inputFeatures?.find((input) => input.name === SeeqNames.Models.InstanceNameInputFeatureName),
    );
    this.state.set('newInputFeatures', []);
    this.state.set('newDerivedFeatures', []);
  };

  /** Sets the template fields from the provided template payload, which may be partial. Only fields present in the payload will be updated. */
  setTemplate = ({ template }: { template: TemplatePayload }): void => {
    const {
      description,
      name,
      inputFeatures,
      derivedFeatures,
      existingDerivedFeatures,
      newInputFeatures,
      newDerivedFeatures,
      existingInputFeatures,
    } = template;

    if (description !== undefined) this.state.set('description', description);
    if (name !== undefined) this.state.set('name', name);
    if (derivedFeatures !== undefined) this.state.set('derivedFeatures', derivedFeatures);
    if (existingDerivedFeatures !== undefined) this.state.set('existingDerivedFeatures', existingDerivedFeatures);
    if (existingInputFeatures !== undefined) this.state.set('existingInputFeatures', existingInputFeatures);
    if (newInputFeatures !== undefined) this.state.set('newInputFeatures', newInputFeatures);
    if (newDerivedFeatures !== undefined) this.state.set('newDerivedFeatures', newDerivedFeatures);

    if (inputFeatures !== undefined) {
      const newInputFeatures = inputFeatures ?? [];

      // CRAB-53616: Ensure the instance name input feature is preserved
      const instanceName = this.state
        .get('inputFeatures')
        .find((f: InputFeatureV1) => f.name === SeeqNames.Models.InstanceNameInputFeatureName);

      const updatedInputFeatures =
        instanceName && !newInputFeatures.some((f) => f.name === SeeqNames.Models.InstanceNameInputFeatureName)
          ? [...newInputFeatures, instanceName]
          : newInputFeatures;

      this.state.set('inputFeatures', updatedInputFeatures);
    }
  };
}
